
/**
 * LEORANA Custom Wishlist
 *
 * Guest users  → localStorage only
 * Logged-in    → localStorage + sync to customer metafield via App Proxy
 *
 * The App Proxy path is /apps/wishlist  (configured in Shopify Partner Dashboard).
 * Shopify forwards requests to api.leorana.com/api/wishlist/* and appends
 * `logged_in_customer_id` when the customer is authenticated.
 */

const LeoWishlist = (() => {
  const STORAGE_KEY = 'leorana_wishlist';
  const PROXY_PATH = '/apps/wishlist';
  let _syncing = false;
  let _initialSyncDone = false;

  // ---------------------------------------------------------------------------
  // localStorage helpers
  // ---------------------------------------------------------------------------

  function getLocal() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  }

  function setLocal(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }

  // ---------------------------------------------------------------------------
  // Server sync (App Proxy)
  // ---------------------------------------------------------------------------

  async function loadFromServer() {
    try {
      const resp = await fetch(PROXY_PATH + '/load', {
        credentials: 'same-origin',
      });
      if (!resp.ok) return null;
      const data = await resp.json();
      return data;
    } catch {
      return null;
    }
  }

  async function saveToServer(items) {
    if (_syncing) return;
    _syncing = true;
    try {
      await fetch(PROXY_PATH + '/save', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
    } catch {
      // Silently fail — localStorage is the fallback
    } finally {
      _syncing = false;
    }
  }

  /**
   * On page load, merge localStorage with server data (if logged in).
   * Server wins for items the customer previously saved on another device;
   * localStorage wins for items added as a guest before logging in.
   */
  async function initialSync() {
    if (_initialSyncDone) return;
    _initialSyncDone = true;

    const serverData = await loadFromServer();
    if (!serverData || !serverData.logged_in) return; // guest — nothing to sync

    const local = getLocal();
    const server = serverData.items || [];

    // Merge: union of both lists, deduplicated
    const merged = [...new Set([...server, ...local])];

    setLocal(merged);
    updateAllButtons();
    updateCounter();

    // If the merged list differs from the server list, save it back
    if (merged.length !== server.length || !merged.every((h) => server.includes(h))) {
      await saveToServer(merged);
    }
  }

  // ---------------------------------------------------------------------------
  // UI updates
  // ---------------------------------------------------------------------------

  function updateAllButtons() {
    const items = getLocal();
    document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
      const handle = btn.dataset.wishlistToggle;
      const active = items.includes(handle);
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', String(active));
    });
  }

  function updateCounter() {
    const count = getLocal().length;
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  function getItems() {
    return getLocal();
  }

  function has(handle) {
    return getLocal().includes(handle);
  }

  function toggle(handle) {
    const items = getLocal();
    const idx = items.indexOf(handle);
    if (idx > -1) {
      items.splice(idx, 1);
    } else {
      items.push(handle);
    }
    setLocal(items);
    updateAllButtons();
    updateCounter();
    saveToServer(items); // fire-and-forget
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items } }));
    return idx === -1; // true = added
  }

  function remove(handle) {
    const items = getLocal().filter((h) => h !== handle);
    setLocal(items);
    updateAllButtons();
    updateCounter();
    saveToServer(items);
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items } }));
  }

  function clear() {
    setLocal([]);
    updateAllButtons();
    updateCounter();
    saveToServer([]);
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items: [] } }));
  }

  // ---------------------------------------------------------------------------
  // Init
  // ---------------------------------------------------------------------------

  function init() {
    updateAllButtons();
    updateCounter();

    // Delegate click handler for all heart buttons
    // Attach direct click handlers on each wishlist button to prevent theme's product-link interception
    function attachWishlistHandlers() {
      document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
        if (btn._wishlistBound) return;
        btn._wishlistBound = true;
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          toggle(btn.dataset.wishlistToggle);
          btn.classList.add('wishlist-btn--pop');
          btn.addEventListener('animationend', () => btn.classList.remove('wishlist-btn--pop'), { once: true });
        }, true);
      });
    }
    attachWishlistHandlers();
    // Re-attach on dynamic content (e.g. infinite scroll, AJAX pagination)
    new MutationObserver(() => attachWishlistHandlers()).observe(document.body, { childList: true, subtree: true });

    // Sync with server (non-blocking)
    initialSync();
  }

  // Auto-init on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  return { getItems, has, toggle, remove, clear, updateAllButtons, updateCounter };
})();
